import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import { founders } from "./peopleData";
import { Linkedin, FileText } from "lucide-react"; // Removed ChevronLeft, ChevronRight
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// --- Removed Custom Arrow Components (SamplePrevArrow, SampleNextArrow) ---

function People() {
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [role, setRole] = useState("Pilot");
  const [reasonForContact, setReasonForContact] = useState(""); // New state for the text area
  const [hoveredFounder, setHoveredFounder] = useState(null);
  const [funFacts, setFunFacts] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const galleryImages = [
    "/gallery/ai_team_pic.png",
    "/gallery/founders_and_malcolm.jpeg",
    "/gallery/bru_burger.jpeg",
    "/gallery/malcolm_mark_jackson.jpeg",
    "/gallery/R2R.jpeg",
    "/gallery/team.jpeg",
    "/gallery/sf.jpeg",
    "/gallery/sf_airport.jpeg",
    "/gallery/hooper.jpeg",
  ];

  const getRandomFunFact = (person) => {
    if (!person.funFacts || person.funFacts.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * person.funFacts.length);
    return person.funFacts[randomIndex];
  };

  const handleMouseEnter = (person) => {
    const fact = getRandomFunFact(person);
    setHoveredFounder(person.name);
    setFunFacts((prev) => ({ ...prev, [person.name]: fact }));
  };

  const handleMouseLeave = () => {
    setHoveredFounder(null);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 900,
    slidesToShow: 2, // Display two images at a time
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2500,
    arrows: false, // Explicitly disabled arrows
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 1, // Display one image on smaller screens
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768, // For tablets and smaller
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false, // Ensure arrows are off on smaller screens too
        }
      },
    ],
  };

  return (
    <section className="text-white min-h-screen flex flex-col items-center relative">
      {/* Animated background effect */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(9, 80, 112, 0.15) 0%, transparent 50%)`
        }}
      />
      
      {/* Hero Section */}
      <div className="max-w-7xl w-full py-12 mt-[11%] px-6 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-block mb-4 animate-slideInLeft">
            <span className="px-4 py-2 rounded-full glass text-sm font-medium text-[#25BC82] neon-border">
              🤝 Meet Our Team
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black animate-fadeInUp leading-tight">
            The Team Behind
            <br />
            <span className="gradient-text">Desync AI</span>
          </h1>
          <p className="text-gray-400 text-lg mt-6 animate-fadeInUp delay-200 max-w-3xl mx-auto">
            The builders behind a better way to extract and use web data.
          </p>
        </div>
      </div>

      {/* Founders Section */}
      <section className="max-w-7xl w-full px-6 py-20 relative">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="text-white">Our Founders</span>
          </h2>
          <p className="text-gray-400 text-lg">The core team building Desync AI from the ground up.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {founders.map((person, idx) => (
            <div key={idx} className="flex flex-col items-center relative text-center"> {/* Centered */}
              <div
                className="relative"
                onMouseEnter={() => handleMouseEnter(person)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" />
                  <img
                    src={person.image}
                    alt={person.name}
                    className="relative w-full h-[400px] object-cover rounded-2xl shadow-xl transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
                <div
                  className={`absolute top-0 left-0 w-full h-full rounded-lg flex items-center justify-center p-4 transition-all duration-300 ease-in-out backdrop-blur-sm
                    ${hoveredFounder === person.name ? "opacity-100 scale-100 bg-black/80" : "opacity-0 scale-95 pointer-events-none"}`}
                >
                  <p className="text-sm text-gray-100 text-center">
                    {funFacts[person.name]}
                  </p>
                </div>
              </div>
              <h3 className="mt-4 text-[24px] font-semibold">{person.name}</h3> {/* Removed text-left w-full */}
              <p className="text-[#25BC82] text-md">{person.position}</p> {/* Removed text-left w-full */}
              {(person.linkedin || ("blog" in person)) && (
                <div className="mt-2 flex items-center gap-4">
                  {person.linkedin && (
                    <a
                      href={person.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#9CA3AF] hover:text-blue-500 transition"
                      aria-label={`${person.name} LinkedIn`}
                    >
                      <Linkedin size={30} />
                    </a>
                  )}
                  {"blog" in person && (
                    <a
                      href={person.blog || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#9CA3AF] hover:text-green-400 transition"
                      aria-label={`${person.name} Blog`}
                    >
                      <FileText size={28} />
                    </a>
                  )}
                </div>
              )}
              {/* {person.bio && (
                <p className="text-[#9CA3AF] text-md mt-2 max-w-[20rem] mx-auto">}
                  {person.bio}
                </p>
              )} */}
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="w-full py-20 bg-gradient-to-br from-[#0a0a0a] to-[#161616]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              <span className="text-white">Team Gallery</span>
            </h2>
            <p className="text-gray-400 text-lg">Behind the scenes with our team</p>
          </div>
          <div>
          <Slider {...sliderSettings}>
            {galleryImages.map((src, i) => (
              <div key={i} className="px-3">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-3xl opacity-0 group-hover:opacity-20 blur-2xl transition-all duration-500" />
                  <img
                    src={src}
                    alt={`Gallery image ${i + 1}`}
                    className="relative w-full h-[400px] object-cover rounded-3xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
            ))}
          </Slider>
          </div>
        </div>
      </section>

      {/* Custom Styles for Slider Dots (Arrow styles fully removed) */}
      <style>{`
        /* Styles for dots */
        .slick-dots li button:before {
          color: white !important;
          font-size: 12px;
        }
      `}</style>

      {/* Let's Connect Form Section */}
      <section id="contact" className="w-full py-20 relative">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative">
            <div className="relative bg-gray-900/50 rounded-3xl p-12 backdrop-blur-xl border border-gray-800 hover:border-green-400 transition-all duration-300">
              <div className="text-center mb-10">
                <h2 className="text-5xl md:text-6xl font-black mb-4">
                  <span className="gradient-text">Let's Connect</span>
                </h2>
                <p className="text-gray-400 text-xl max-w-2xl mx-auto">Whether you're interested in a pilot, developer partnership, or integration — we'd love to hear from you.</p>
              </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              // Include reasonForContact in formData
              const formData = { email, role, reasonForContact };

              try {
                await fetch("https://submit-form.com/OOy1NWTF1", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(formData),
                });

                setSuccessMessage("Thanks! We'll be in touch.");
                setEmail("");
                setRole("Pilot");
                setReasonForContact(""); // Clear the textarea after submission
              } catch {
                setSuccessMessage("Thanks! We'll be in touch.");
                setEmail("");
                setRole("Pilot");
                setReasonForContact(""); // Clear the textarea even on error fallback
              }
            }}
            className="max-w-3xl mx-auto"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="group">
                <label className="block text-sm font-medium text-gray-400 mb-2">Project Type</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200"
                  required
                >
                  <option value="Pilot">Pilot Project</option>
                  <option value="Developer">Developer</option>
                  <option value="Integration Partner">Partner</option>
                </select>
              </div>
              <div className="group">
                <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200"
                  required
                />
              </div>
            </div>
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-400 mb-2">Message</label>
              <textarea
                placeholder="Tell us about your interest in our team, partnership opportunities, or any questions you have..."
                value={reasonForContact}
                onChange={(e) => setReasonForContact(e.target.value)}
                rows="5"
                className="w-full px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#25BC82] focus:bg-gray-800/80 transition-all duration-200 resize-none"
              />
            </div>
            <div className="text-center">
              <button
                type="submit"
                className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-[#095070] to-[#25BC82] text-white px-10 py-5 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#25BC82]/20"
              >
                <span>Send Message</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </form>

              {successMessage && (
                <div className="mt-8 p-5 bg-green-500/10 backdrop-blur-sm rounded-xl border border-green-500/20 max-w-2xl mx-auto">
                  <p className="text-green-400 text-center text-lg font-medium">{successMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

export default People;
